import {
  View,
  Text,
  TouchableOpacity,
  Image,
  useWindowDimensions,
} from "react-native";
import { router, Href } from "expo-router";
import React, { useState } from "react";
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSpring,
} from "react-native-reanimated";

interface StartComp {
  title: string;
  IconComponent?: React.ComponentType<{
    width: number;
    height: number;
    fill: string;
  }>;
  iconSource?: any; // For PNG/JPG images
  url: string;
}

const StartComp = ({ title, IconComponent, iconSource, url }: StartComp) => {
  const [iconError, setIconError] = useState(false);
  const scale = useSharedValue(1);
  const { width: screenWidth } = useWindowDimensions();
  // Responsive: 2 cards per row with gap, max 160px
  const cardSize = Math.min(Math.floor((screenWidth - 60) / 2), 160);
  const iconSize = Math.min(Math.floor(cardSize * 0.55), 95);
  const imageSize = Math.min(Math.floor(cardSize * 0.8), 135);

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }],
  }));

  // Render icon based on type (SVG component or PNG image)
  const renderIcon = () => {
    try {
      // If we have an image source (PNG/JPG), use Image component
      if (iconSource) {
        return (
          <Image
            source={iconSource}
            style={{
              width: imageSize,
              height: imageSize,
              marginTop: 10,
              resizeMode: "contain",
            }}
            key={`${title}-image-static`}
            fadeDuration={0}
            onError={(error) => {
              setIconError(true);
            }}
          />
        );
      }

      // If we have an SVG component, render it
      if (IconComponent) {
        return React.createElement(IconComponent, {
          key: `${title}-icon`,
          width: iconSize,
          height: iconSize,
          fill: "#374151",
        });
      }

      // Fallback if neither is provided
      throw new Error("No icon provided");
    } catch (error) {
      setIconError(true);
      return (
        <View
          style={{
            width: iconSize,
            height: iconSize,
            backgroundColor: "#f3f4f6",
            borderRadius: 12,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Text style={{ fontSize: 12, color: "#666" }}>Icon Error</Text>
        </View>
      );
    }
  };

  return (
    <Animated.View
      style={[
        { 
          borderRadius: 24, 
          backgroundColor: "#fff", 
          overflow: "visible", 
          shadowColor: "#000",
          shadowOffset: { width: 0, height: 6 },
          shadowOpacity: 0.15,
          shadowRadius: 12,
          elevation: 8,
        },
        animatedStyle,
      ]}
    >
      <TouchableOpacity
        activeOpacity={0.8}
        style={{
          borderRadius: 24,
          display: "flex",
          width: cardSize,
          height: cardSize,
          alignItems: "center",
          justifyContent: "center",
        }}
        onPressIn={() => {
          scale.value = withSpring(0.92, { damping: 12, stiffness: 200, mass: 0.5 });
        }}
        onPressOut={() => {
          scale.value = withSpring(1, { damping: 10, stiffness: 250, mass: 0.5 });
        }}
        onPress={() => {
          router.push(url as Href);
        }}
      >
        <View
          style={{ alignItems: "center", justifyContent: "center", flex: 1 }}
        >
          <View
            style={{
              height: iconSize,
              width: iconSize,
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 8,
            }}
          >
            {renderIcon()}
          </View>
          <Text className="text-black text-center text-lg font-[900]">
            {title}
          </Text>
        </View>
      </TouchableOpacity>
    </Animated.View>
  );
};

export default StartComp;
